import { Observable, throwError, Subject } from 'rxjs';
import { Injectable, ApplicationRef, NgZone } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { catchError, tap, finalize, shareReplay } from 'rxjs/operators';
import { SpinnerService } from 'src/app/shared/service/spinner-service';
import { Router } from '@angular/router';
import { TokenService } from 'src/app/shared/service/token.service';

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
  private pendingRequests = 0;
  private requestCache = new Map<string, Subject<HttpEvent<any>>>();
  private staticToken = '8f9b3c7e4d9a1b0c9a7f51c7c2b83cbe';

  constructor(
    private spinnerService: SpinnerService,
    private route: Router,
    private tokenService: TokenService,
    private appRef: ApplicationRef,
    private ngZone: NgZone
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.tokenService.getToken() || this.staticToken;
    const requestKey = this.getRequestKey(req);

    // Prevent duplicate API calls
    if (this.requestCache.has(requestKey)) {
      return this.requestCache.get(requestKey)!.asObservable();
    }

    const requestSubject = new Subject<HttpEvent<any>>();
    this.requestCache.set(requestKey, requestSubject);

    if (this.pendingRequests === 0) {
      this.spinnerService.show();
    }
    this.pendingRequests++;

    const clonedRequest = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });

    return next.handle(clonedRequest).pipe(
      shareReplay(1), // Ensures multiple subscribers get the same response
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
         /* console.log('🔹 API Response Received (Raw):', event.body);*/

          // Deep Clone Response to Prevent Mutation
          const responseClone = JSON.parse(JSON.stringify(event.body));

          /*console.log('✅ Final Response Before Passing:', responseClone);*/

          this.completeRequest(requestKey, new HttpResponse({
            body: responseClone,
            headers: event.headers,
            status: event.status,
            statusText: event.statusText,
            url: event.url || undefined
          }));
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ HTTP Error:', error);

        if (error.status === 500) {
          console.warn('⚠️ 500 Error - Retrying with Static Token');

          const retryRequest = req.clone({
            setHeaders: { Authorization: `Bearer ${this.staticToken}` },
          });

          return next.handle(retryRequest).pipe(
            tap((event: HttpEvent<any>) => {
              if (event instanceof HttpResponse) {
                this.completeRequest(requestKey, event);
              }
            }),
            catchError((retryError) => {
              console.error('❌ Retry Failed:', retryError);
              this.failRequest(requestKey, retryError);
              return throwError(() => retryError);
            })
          );
        } else if (error.status === 401) {
          console.warn('⚠️ 401 Unauthorized - Redirecting to Login');
          this.route.navigate(['/login']);
        }

        this.failRequest(requestKey, error);
        return throwError(() => error);
      }),
      finalize(() => {
        this.requestCache.delete(requestKey);
      })
    );
  }

  private completeRequest(requestKey: string, event: HttpResponse<any>) {
    this.decreasePendingRequests();

    // ✅ Deep Clone the response to prevent modification issues
    const finalResponse = JSON.parse(JSON.stringify(event.body));

    // ✅ Log the full final API response
    //console.log('✅ Final API Response Before Passing to Subscribers:', {
    //  url: event.url,
    //  status: event.status,
    //  body: finalResponse // Ensure complete data is logged
    //});

    this.ngZone.run(() => {
      setTimeout(() => {
        this.appRef.tick();
      }, 0);
    });

    const requestSubject = this.requestCache.get(requestKey);
    if (requestSubject) {
      requestSubject.next(event);
      requestSubject.complete();
    }
  }

  private failRequest(requestKey: string, error: HttpErrorResponse) {
    this.decreasePendingRequests();
    const requestSubject = this.requestCache.get(requestKey);
    if (requestSubject) {
      requestSubject.error(error);
    }
    this.requestCache.delete(requestKey);
  }

  private decreasePendingRequests(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);

    // Delay hiding the loader slightly to prevent flickering
    if (this.pendingRequests === 0) {
      setTimeout(() => {
        if (this.pendingRequests === 0) {
          this.spinnerService.hide();
        }
      }, 300);
    }
  }

  private getRequestKey(req: HttpRequest<any>): string {
    return `${req.method}-${req.urlWithParams}`;
  }
}
